/**
 * Разбор ссылок на публичный профиль (стори 18.3).
 *
 * <p>Ссылка на профиль приходит тремя разными путями, и все три ведут в одно и то же место:
 * <ul>
 *   <li>`svyaz://u/<ник>` — диплинк с публичной страницы в установленное приложение;
 *   <li>`https://vrmsgr.pro/u/<ник>` — сама страница; на устройстве с приложением её открывает
 *       система (universal / app link), в браузере это серверный HTML;
 *   <li>`https://vrmsgr.pro/?u=<ник>` — веб-клиент: путь `/u/` занят страницей, поэтому в SPA
 *       ник уходит параметром.
 * </ul>
 *
 * <p>Ни React, ни состояния: правило «что считать ссылкой на профиль» проверяется тестом.
 * Иначе убедиться, что чужая ссылка не открывает диалог, можно было бы только на устройстве.
 */

import { isValidPublicHandle, stripHandlePrefix } from './publicHandle';

/**
 * Ник из ссылки: снимает `#`, пробелы и регистр — и на этом останавливается.
 *
 * <p>Дальше только проверка правилами из {@link isValidPublicHandle}: додумывать за сервер
 * (заменять дефисы, обрезать длину) здесь нельзя. В ссылке лежит уже сохранённый ник, и
 * «исправленный» на лету откроет диалог не с тем человеком.
 */
export function normalizePublicHandle(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const h = stripHandlePrefix(raw).toLowerCase();
  return isValidPublicHandle(h) ? h : null;
}

/**
 * Ник из ссылки или `null`, если это не наша ссылка на профиль.
 *
 * <p>Хост не проверяется намеренно: домен сайта настраивается (`PUBLIC_WEB_ORIGIN`), на стенде
 * он другой, а вреда нет — открытие диалога всё равно упирается в существующего пользователя с
 * этим ником. Проверяется форма: путь `/u/<ник>` или параметр `?u=<ник>` плюс алфавит ника.
 */
export function handleFromProfileLink(href: string | null | undefined): string | null {
  if (!href) return null;
  const raw = href.trim();
  if (raw.length === 0) return null;

  // `svyaz://u/zorro` — URL с нестандартной схемой: у него пустой host в одних движках и
  // host === 'u' в других, поэтому разбираем строкой, а не через URL.
  const scheme = raw.match(/^([a-z][a-z0-9+.-]*):\/\/(.*)$/i);
  if (scheme && !/^https?$/i.test(scheme[1])) {
    const rest = scheme[2].replace(/^\/+/, '');
    const m = rest.match(/^u\/([^/?#]+)/i);
    return m ? normalizePublicHandle(decodeURIComponent(m[1])) : null;
  }

  try {
    const url = new URL(raw);
    const fromQuery = url.searchParams.get('u');
    if (fromQuery) {
      const h = normalizePublicHandle(fromQuery);
      if (h) return h;
    }
    const m = url.pathname.match(/^\/u\/([^/]+)\/?$/);
    return m ? normalizePublicHandle(decodeURIComponent(m[1])) : null;
  } catch {
    return null;
  }
}
